import type { OvertimeDayRates } from "@laborchart-modules/common/dist/rethink/schemas/companies";
import { formatDateTime } from "@procore/globalization-toolkit";
import { timeFormat } from "d3-time-format";
import { getBoardsConfigurePanelValues } from "./configure-panel";
import { COLUMN_WIDTHS } from "./configure-panel";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime/date";
import { formatMilitaryTimeValue } from "@laborchart-modules/common/dist/datetime";
import { BoardsTaskType } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-boards";
import type { BoardsFilterTypes } from "./filter-panel";
import type { BryntumTaskBoard } from "@bryntum/taskboard-react-thin";
import type React from "react";
import type { ColumnProject, Assignment, ResourceBreakdownItem } from "./types";
import type { NumberOptions } from "@procore/globalization-toolkit";
import { NumberFormatter } from "@procore/globalization-toolkit";
import type { TaskModel } from "@bryntum/taskboard-thin";
import { publish } from "./hooks/boards-event-bus";

const DEFAULT_JOB_TITLE_SEQUENCE = 999;

export const BOARDS_CONFIGURE_PANEL_KEY = "boards-configure-panel";
export const BOARDS_CONFIGURE_PANEL_UPDATE_EVENT = "boards-configure-panel-update";

export function getDatesRangeString(startDate: Date, endDate: Date | null, I18n: any) {
   const opts = { locale: I18n.locale };
   // Set end date to "TBD" if it's null or undefined
   const formattedEndDate = endDate ? formatDateTime(endDate, opts) : "TBD";
   return `${formatDateTime(startDate, opts)} - ${formattedEndDate}`;
}

export function formatTimeString(time: string): string {
   const formatTime = timeFormat("%-I:%M %p"); // formats the time to "h:mm am/pm" (eg. 1:30 pm)
   const date = new Date();
   try {
      const [hours, minutes, seconds] = time.split(":").map(Number);
      date.setHours(hours, minutes, seconds, 0);
   } catch (e) {
      console.error("Error parsing time string:", time, e);
   }
   return formatTime(date);
}

export function getDailyRateAndBurn(
   project: ColumnProject,
   assignments: Assignment[],
   paidShiftHours: number,
   overtimeDayRates: OvertimeDayRates | null,
   selectedDate: Date,
) {
   const wageOverrides = project?.wage_overrides || [];

   // Calculate daily hours from start and end times
   const calculateHoursFromTimeString = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return hours + minutes / 60;
   };

   let totalDailyCost = 0;
   let cumulativeHours = 0;

   // Get the day of week (0 = Sunday, 1 = Monday, etc.)
   const dayOfWeek = selectedDate.getDay();
   const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
   const currentDayName = dayNames[dayOfWeek];
   const overtimeRate = overtimeDayRates?.[currentDayName as keyof OvertimeDayRates] || 1;

   assignments.forEach((assignment: any) => {
      // Calculate hours for the assignment
      let totalAssignmentHours;
      if (assignment.percent_allocated) {
         // Calculate hours based on percentage of paidShiftHours
         totalAssignmentHours = (assignment.percent_allocated / 100) * paidShiftHours;
      } else if (assignment.start_time && assignment.end_time) {
         // Calculate hours from start and end times
         const startHours = calculateHoursFromTimeString(assignment.start_time);
         const endHours = calculateHoursFromTimeString(assignment.end_time);
         if (startHours < endHours) {
            // Normal same-day shift
            totalAssignmentHours = endHours - startHours;
         } else {
            // Overnight shift (end time is on the next calendar day)
            totalAssignmentHours = 24 - startHours + endHours;
         }
         // NOTE: Do not cap here — the overtime path needs uncapped hours to split
         // into regular vs overtime. Capping is handled within each cost branch below.
      } else {
         totalAssignmentHours = paidShiftHours;
      }

      // Determine hourly rate based on priority
      let hourlyRate = 0;

      // 1. Check wage override (highest priority)
      const wageOverride = wageOverrides.find(
         (override: any) => override.position_id === assignment.job_title_id,
      );
      if (wageOverride) {
         hourlyRate = wageOverride.rate;
      }
      // 2. Check resource's hourly wage
      else if (assignment.resource_hourly_wage) {
         hourlyRate = assignment.resource_hourly_wage;
      }
      // 3. Check assignment's job title rate
      else if (assignment.job_title_rate) {
         hourlyRate = assignment.job_title_rate;
         // 4. Check equipment assignment's rate
      } else if (assignment.equipment_rate_per_hour) {
         hourlyRate = assignment.equipment_rate_per_hour;
      }

      // Skip assignments with no rate so salaried/rate-less resources don't skew the average
      if (hourlyRate === 0) {
         return {
            dailyRate: 0,
            dailyBurn: 0,
         };
      }

      let totalAssignmentCost;

      // If overtime rates are set up (not null), calculate regular and overtime hours.
      // We don't apply overtime rates to equipment assignments.
      if (overtimeDayRates !== null && assignment.type !== BoardsTaskType.EQUIPMENT_ASSIGNMENT) {
         const regularHours = Math.min(totalAssignmentHours, paidShiftHours);
         const overtimeHours = Math.max(0, totalAssignmentHours - paidShiftHours);
         const regularCost = hourlyRate * regularHours;
         const overtimeCost = hourlyRate * overtimeRate * overtimeHours;
         totalAssignmentCost = regularCost + overtimeCost;
         // Accumulate full hours (regular + overtime) for the weighted average
         cumulativeHours += totalAssignmentHours;
      } else {
         // Cap hours at paidShiftHours when no overtime rates are configured
         const cappedHours = Math.min(totalAssignmentHours, paidShiftHours);
         totalAssignmentCost = hourlyRate * cappedHours;
         cumulativeHours += cappedHours;
      }

      // Accumulate this assignment's cost into the project total
      totalDailyCost += totalAssignmentCost;
   });

   // Daily rate = weighted average hourly rate across all assignments (totalCost / totalHours)
   const dailyRate = cumulativeHours > 0 ? totalDailyCost / cumulativeHours : 0;

   // Daily burn = total labor cost for the day
   const dailyBurn = totalDailyCost;

   return {
      dailyRate,
      dailyBurn,
   };
}

export function formatPhoneNumberForTaskCard(phoneNumber: string): string {
   // Remove all non-digit characters
   const digits = phoneNumber.replace(/\D/g, "");

   // Extract the last 10 digits (US phone number format)
   const lastTenDigits = digits.slice(-10);

   // Format as XXX-XXX-XXXX
   return `${lastTenDigits.slice(0, 3)}-${lastTenDigits.slice(3, 6)}-${lastTenDigits.slice(6)}`;
}

export function maybeUpdateColumnWidths() {
   const columnWidth = getBoardsConfigurePanelValues().columnWidth;
   const widthValue = COLUMN_WIDTHS[columnWidth];

   // Find the taskboard element and update the CSS custom property
   const taskboardElement = document.querySelector(".b-task-board") as HTMLElement;
   if (taskboardElement) {
      taskboardElement.style.setProperty("--boards-column-width", `${widthValue}px`);
   }
}

export function getTagsFromIds(tagIds: string[], taskBoardRef: any) {
   const tags = taskBoardRef.current?.instance.company.tag_options.filter((tag: any) =>
      tagIds.includes(tag.id),
   );
   return tags;
}

export function tearsheetRequestToTask(request: any, taskBoardRef: any) {
   return {
      ...request,
      type: BoardsTaskType.REQUEST,
      id: request.id,
      project_id: request.projectId,
      start_day: getDetachedDay(request.startDate),
      end_day: getDetachedDay(request.endDate),
      job_title_name: request.jobTitleName,
      job_title_color: request.jobTitleColor,
      request_tags: getTagsFromIds(request.tagIds, taskBoardRef),
      work_days: request.workDays,
      ...(request.percentAllocated
         ? { percent_allocated: request.percentAllocated }
         : { start_time: request.startTime, end_time: request.endTime }),
   };
}

export function tearsheetEquipmentRequestToTask(request: any) {
   return {
      ...request,
      type: BoardsTaskType.EQUIPMENT_REQUEST,
      id: request.id,
      project_id: request.projectId,
      start_day: getDetachedDay(request.startDate),
      end_day: getDetachedDay(request.endDate),
      equipment_type_id: request.typeId,
      equipment_type_name: request.equipmentTypeName,
      work_days: request.workDays,
      category_id: request.categoryId ?? null,
      subcategory_id: request.subcategoryId ?? null,
      ...(request.percentAllocated
         ? { percent_allocated: request.percentAllocated }
         : { start_time: request.startTime, end_time: request.endTime }),
   };
}

export function transformFiltersForBackend(filters: BoardsFilterTypes) {
   const queryFilters: any = {};

   if (filters.selectedProjects && filters.selectedProjects.length > 0) {
      queryFilters.selected_project_ids = filters.selectedProjects.map((project) => project.id);
   }

   // Map hideEmptyProjects to hide_empty_projects
   if (filters.hideEmptyProjects === true) {
      queryFilters.hide_empty_projects = true;
   }

   // Map resourceStatuses to resource_status (convert to lowercase)
   if (filters.resourceStatuses && filters.resourceStatuses.length > 0) {
      queryFilters.resource_status = filters.resourceStatuses.map((status) =>
         status.name.toLowerCase(),
      );
   }

   // Map hide flags to hidden_entities array (using Set to avoid duplicates)
   const hiddenEntities = new Set<string>();
   if (filters.hideAssignments) hiddenEntities.add("assignments");
   if (filters.hideRequests) hiddenEntities.add("requests");
   if (filters.hideEquipment) hiddenEntities.add("equipment");
   if (filters.hideLabor) {
      hiddenEntities.add("assignments");
      hiddenEntities.add("requests");
   }

   if (hiddenEntities.size > 0) {
      queryFilters.hidden_entities = [...hiddenEntities];
   }

   if (filters.projectTags && filters.projectTags.length > 0) {
      // map tags to ids
      const tagIds = filters.projectTags.map((tag) => tag.id);
      queryFilters.project_tag_ids = tagIds;
   }

   if (filters.jobTitles && filters.jobTitles.length > 0) {
      queryFilters.job_title_ids = filters.jobTitles.map((jobTitle) => jobTitle.id);
   }

   if (filters.projectRoles && filters.projectRoles.length > 0) {
      queryFilters.project_roles = filters.projectRoles
         .filter((role: any) => role.resource_id && role.job_title_id)
         .map((role: any) => ({
            resource_id: role.resource_id,
            job_title_id: role.job_title_id,
         }));
   }

   if (filters.projectCustomFields && filters.projectCustomFields.length > 0) {
      queryFilters.project_custom_fields = filters.projectCustomFields;
   }

   return Object.keys(queryFilters).length > 0 ? queryFilters : undefined;
}

export function editedTearsheetRequestToTask(
   data: any,
   task: any,
   taskBoardRef: React.RefObject<BryntumTaskBoard>,
): void {
   const updatedTask = data.newTasks[0];
   if (!updatedTask) return;
   const isEquipmentRequest = updatedTask.type === BoardsTaskType.EQUIPMENT_REQUEST;

   // This means that the request was filled via the tearsheet.
   if (updatedTask.type == BoardsTaskType.ASSIGNMENT) {
      const taskBoard = taskBoardRef.current?.instance;
      const projectModel = taskBoard?.project as any;
      const column = taskBoard?.columns.getById(task.getData("project_id"));

      // Suspend auto-sync for both the remove and add since the tearsheet
      // already persisted the changes to the backend.
      projectModel?.suspendAutoSync();
      task.remove();

      const newTaskData = {
         id: updatedTask.id,
         type: BoardsTaskType.ASSIGNMENT,
         name: updatedTask.name,
         project_id: updatedTask.projectId,
         resource_id: updatedTask.resourceId,
         start_day: updatedTask.startDate ? getDetachedDay(updatedTask.startDate) : null,
         end_day: updatedTask.endDate ? getDetachedDay(updatedTask.endDate) : null,
         start_time:
            updatedTask.startTime != null
               ? formatMilitaryTimeValue(updatedTask.startTime)
               : task.getData("start_time"),
         end_time:
            updatedTask.endTime != null
               ? formatMilitaryTimeValue(updatedTask.endTime)
               : task.getData("end_time"),
         job_title_name: updatedTask.jobTitleName,
         job_title_color: updatedTask.jobTitleColor,
         category_id: updatedTask.categoryId ?? null,
         subcategory_id: updatedTask.subcategoryId ?? null,
         instruction_text: updatedTask.instructionText,
         work_scope_text: updatedTask.workScopeText,
         work_days: updatedTask.workDays,
         employee_number: updatedTask.employeeNumber ?? null,
         phone_number: updatedTask.phoneNumber ?? null,
         resource_address_1: updatedTask.resourceAddress1 ?? null,
         resource_address_2: updatedTask.resourceAddress2 ?? null,
         resource_city_town: updatedTask.resourceCityTown ?? null,
         resource_hourly_wage: updatedTask.resourceHourlyWage ?? null,
         resource_state_province: updatedTask.resourceStateProvince ?? null,
         resource_zipcode: updatedTask.resourceZipcode ?? null,
         ...(updatedTask.percentAllocated != null
            ? { percent_allocated: updatedTask.percentAllocated }
            : {}),
      };

      if (column) {
         taskBoard?.addTask(column as any, undefined, newTaskData);
      }

      projectModel?.acceptChanges();
      projectModel?.resumeAutoSync();
      publish("column-tasks-changed", { projectId: updatedTask.projectId });
      return;
   }

   const taskId = task.getData("id");
   const tagsIds = updatedTask.tagIds
      ? Object.keys(updatedTask.tagIds).filter((key) => updatedTask.tagIds![key])
      : [];

   // Update task properties with data from tearsheet
   const updatedData = {
      start_day: updatedTask.startDate
         ? getDetachedDay(updatedTask.startDate)
         : task.getData("start_day"),
      end_day: updatedTask.endDate ? getDetachedDay(updatedTask.endDate) : task.getData("end_day"),
      start_time: updatedTask.startTime || task.getData("start_time"),
      end_time: updatedTask.endTime || task.getData("end_time"),
      instruction_text: updatedTask.instructionText || task.getData("instruction_text"),
      work_scope_text: updatedTask.workScopeText || task.getData("work_scope_text"),
      work_days: updatedTask.workDays || task.getData("work_days"),
      job_title_name: updatedTask.jobTitleName || task.getData("job_title_name"),
      job_title_color: updatedTask.jobTitleColor || task.getData("job_title_color"),
      request_tags:
         tagsIds.length > 0 ? getTagsFromIds(tagsIds, taskBoardRef) : task.getData("request_tags"),
      ...(updatedTask.percentAllocated !== undefined
         ? { percent_allocated: updatedTask.percentAllocated }
         : {}),
      ...(isEquipmentRequest
         ? {
              equipment_type_id: updatedTask.equipmentTypeId,
              equipment_type_name: updatedTask.equipmentTypeName,
           }
         : {}),
   };

   task.set(updatedData);
   // Clear the "modified" flags so Bryntum doesn't sync these -- the tearsheet already saved to the backend
   task.clearChanges();
   task.endBatch();
   publish("task-updated", { taskId });
   publish("column-tasks-changed", { projectId: task.getData("project_id") });
}

export function editedTearsheetAssignmentToTask(data: any, task: any): void {
   const taskId = task.getData("id");

   // Update task properties with data from tearsheet
   const updatedData = {
      name: data.newTask?.name || task.getData("name"),
      project_id: data.newTask?.projectId || task.getData("project_id"),
      resource_id: data.newTask?.resourceId || task.getData("resource_id"),
      start_day: data.newTask?.startDate
         ? getDetachedDay(data.newTask.startDate)
         : task.getData("start_day"),
      end_day: data.newTask?.endDate
         ? getDetachedDay(data.newTask.endDate)
         : task.getData("end_day"),
      start_time: data.newTask?.startTime || task.getData("start_time"),
      end_time: data.newTask?.endTime || task.getData("end_time"),
      instruction_text: data.newTask?.instructionText || task.getData("instruction_text"),
      work_scope_text: data.newTask?.workScopeText || task.getData("work_scope_text"),
      work_days: data.newTask?.workDays || task.getData("work_days"),
      job_title_name: data.newTask?.jobTitleName || task.getData("job_title_name"),
      job_title_color: data.newTask?.jobTitleColor || task.getData("job_title_color"),
      ...(data.newTask?.percentAllocated !== undefined
         ? { percent_allocated: data.newTask.percentAllocated }
         : {}),
   };

   task.set(updatedData);
   task.clearChanges();
   task.endBatch();

   publish("task-updated", { taskId });
   publish("column-tasks-changed", { projectId: task.getData("project_id") });
}

export function createdTearsheetAssignmentToTask(data: any, originalTask: TaskModel): TaskModel {
   return {
      ...(originalTask as any).data,
      id: data.id,
      type: BoardsTaskType.ASSIGNMENT,
      name: data.name,
      project_id: data.project_id,
      start_day: data.start_day,
      end_day: data.end_day,
      category_id: data.category_id,
      subcategory_id: data.subcategory_id,
      start_time:
         data.start_time != null
            ? formatMilitaryTimeValue(data.start_time)
            : originalTask.getData("start_time"),
      end_time:
         data.end_time != null
            ? formatMilitaryTimeValue(data.end_time)
            : originalTask.getData("end_time"),
      instruction_text: data.instruction_text,
      work_scope_text: data.work_scope_text,
      work_days: data.work_days,
      ...(data.newTask?.percentAllocated !== undefined
         ? { percent_allocated: data.newTask.percentAllocated }
         : {}),
   } as unknown as TaskModel;
}

export function editedTearsheetEquipmentAssignmentToTask(data: any, task: any): void {
   const taskId = task.getData("id");

   // Update task properties with data from tearsheet
   const updatedData = {
      equipment_name: data.newTask?.equipmentName || task.getData("equipment_name"),
      project_id: data.newTask?.projectId || task.getData("project_id"),
      resource_id: data.newTask?.resourceId || task.getData("resource_id"),
      equipment_type_id: data.newTask?.equipmentTypeId || task.getData("equipment_type_id"),
      equipment_type_name: data.newTask?.equipmentTypeName || task.getData("equipment_type_name"),
      equipment_rate_per_hour:
         data.newTask?.equipmentRatePerHour || task.getData("equipment_rate_per_hour"),
      start_day: data.newTask?.startDate
         ? getDetachedDay(data.newTask.startDate)
         : task.getData("start_day"),
      end_day: data.newTask?.endDate
         ? getDetachedDay(data.newTask.endDate)
         : task.getData("end_day"),
      start_time: data.newTask?.startTime || task.getData("start_time"),
      end_time: data.newTask?.endTime || task.getData("end_time"),
      work_days: data.newTask?.workDays || task.getData("work_days"),
      percent_allocated: data.newTask?.percentAllocated || task.getData("percent_allocated"),
   };

   task.set(updatedData);
   task.clearChanges();
   task.endBatch();

   publish("task-updated", { taskId });
   publish("column-tasks-changed", { projectId: task.getData("project_id") });
}

export function tearsheetPersonToTaskCardFields(params: {
   taskStore: { forEach: (fn: (task: any) => void) => void };
   person: any;
   resourceId: string;
   lastNameFirst: boolean;
   taskBoardRef: React.RefObject<BryntumTaskBoard>;
}): void {
   const { taskStore, person, resourceId, lastNameFirst, taskBoardRef } = params;

   const projectIds = new Set<string>();

   taskStore.forEach((task: any) => {
      if (task.getData("resource_id") !== resourceId) return;
      if (task.getData("type") !== BoardsTaskType.ASSIGNMENT) return;

      const tags =
         getTagsFromIds(person?.tag_instances?.map((tag: any) => tag.tag_id) ?? [], taskBoardRef) ??
         task.getData("resource_tags");

      task.set({
         name: lastNameFirst
            ? `${person.name.last}, ${person.name.first}`
            : `${person.name.first} ${person.name.last}`,
         job_title_name: person.job_title?.name ?? task.getData("job_title_name"),
         job_title_color: person.job_title?.color ?? task.getData("job_title_color"),
         employee_number: person.employee_number ?? task.getData("employee_number"),
         resource_tags: tags,
         phone_number: person.phone ?? task.getData("phone_number"),
         resource_address_1: person.address_1 ?? task.getData("resource_address_1"),
         resource_address_2: person.address_2 ?? task.getData("resource_address_2"),
         resource_city_town: person.city_town ?? task.getData("resource_city_town"),
         resource_state_province: person.state_province ?? task.getData("resource_state_province"),
         resource_zipcode: person.zipcode ?? task.getData("resource_zipcode"),
         resource_hourly_wage: person.hourly_wage ?? task.getData("resource_hourly_wage"),
      });
      task.clearChanges();

      publish("task-updated", { taskId: task.getData("id") });
      projectIds.add(task.getData("project_id"));
   });

   // Published after the loop so all task data is updated before column toolbars re-render
   projectIds.forEach((projectId) => publish("column-tasks-changed", { projectId }));
}

export function getResourceBreakdownData(tasks: any) {
   const assignmentTasks =
      tasks?.filter((task: any) => task.getData("type") === BoardsTaskType.ASSIGNMENT) ?? [];
   const requestTasks =
      tasks?.filter((task: any) => task.getData("type") === BoardsTaskType.REQUEST) ?? [];
   const assignmentCount = assignmentTasks.length;
   const requestCount = requestTasks.length;

   const buildBreakdown = (taskList: any[]) =>
      taskList.reduce((acc: Record<string, ResourceBreakdownItem>, task: any) => {
         const jobTitleName = task.getData("job_title_name");
         const jobTitleColor = task.getData("job_title_color");
         const jobTitleSequence = task.getData("job_title_sequence");

         if (jobTitleName) {
            if (!acc[jobTitleName]) {
               acc[jobTitleName] = {
                  name: jobTitleName,
                  color: jobTitleColor || null,
                  count: 0,
                  sequence: jobTitleSequence ?? DEFAULT_JOB_TITLE_SEQUENCE,
               };
            }
            acc[jobTitleName].count++;
         }

         return acc;
      }, {} as Record<string, ResourceBreakdownItem>);

   const assignmentBreakdown = buildBreakdown(assignmentTasks);
   const requestBreakdown = buildBreakdown(requestTasks);

   const assignmentBreakdownArray: ResourceBreakdownItem[] = (
      Object.values(assignmentBreakdown) as ResourceBreakdownItem[]
   ).sort((a, b) => a.sequence - b.sequence);

   const requestBreakdownArray: ResourceBreakdownItem[] = (
      Object.values(requestBreakdown) as ResourceBreakdownItem[]
   ).sort((a, b) => a.sequence - b.sequence);

   return {
      assignmentBreakdownArray,
      requestBreakdownArray,
      assignmentCount,
      requestCount,
   };
}

/**
 * Sorts assignments and requests within each project column by `job_title_sequence`.
 *
 * A project column can contain:
 *  - Assignments/requests directly under the project (no category)
 *  - Category headers with assignments/requests nested beneath them
 *  - Subcategory headers (categories with a parent_id) with their own nested assignments/requests
 *
 * This function groups all tasks by their business-level `project_id` (each project
 * is a column on the board), splits each group into "sections" at category boundaries,
 * then sorts the non-category tasks within each section by `job_title_sequence` (primary)
 * and `name` in ascending alphabetical order.
 * Category/subcategory header positions are preserved — only the assignments/requests
 * underneath them are reordered.
 *
 * `weight` is Bryntum's built-in field that controls the visual ordering of tasks
 * within a column. After sorting by `job_title_sequence`, we reassign sequential
 * `weight` values (with gaps of 100) so Bryntum renders them in the desired order.
 *
 * Weight values are reassigned silently (third arg `true` in Bryntum's `set`)
 * so they are not tracked as modifications and won't trigger a sync to the backend.
 *
 * @param taskBoardRef - React ref to the BryntumTaskBoard component instance
 */
export function sortTasksByJobTitleSequence(taskBoardRef: React.RefObject<BryntumTaskBoard>) {
   const taskBoard = taskBoardRef.current?.instance;
   if (!taskBoard) return;

   // Bryntum's ProjectModel — the single data layer that owns all stores (tasks, resources, etc.).
   // This is NOT a business-level "project"; those are represented as columns on the board.
   const projectModel = taskBoard.project as any;
   const taskStore = projectModel?.taskStore;
   if (!taskStore) return;

   projectModel.suspendAutoSync();

   // Groups tasks by their business-level project_id (each key = one column on the board)
   const tasksByProject = new Map<string, any[]>();
   taskStore.forEach((task: any) => {
      const projectId = task.getData("project_id");
      if (!tasksByProject.has(projectId)) {
         tasksByProject.set(projectId, []);
      }
      tasksByProject.get(projectId)!.push(task);
   });

   const WEIGHT_GAP = 100;

   for (const tasks of tasksByProject.values()) {
      tasks.sort((a: any, b: any) => (a.weight ?? 0) - (b.weight ?? 0));

      // Splits tasks into sections at category boundaries. Each section is either:
      //  - A category header followed by its assignments/requests, or
      //  - A group of uncategorized assignments/requests
      const sections: any[][] = [];
      let currentSection: any[] = [];

      for (const task of tasks) {
         if (task.getData("type") === BoardsTaskType.CATEGORY) {
            if (currentSection.length > 0) {
               sections.push(currentSection);
            }
            currentSection = [task];
         } else {
            currentSection.push(task);
         }
      }
      if (currentSection.length > 0) {
         sections.push(currentSection);
      }

      // weight: Bryntum's ordering field — sequential values control visual position in the column
      let weight = 0;
      for (const section of sections) {
         const startsWithCategory = section[0]?.getData("type") === BoardsTaskType.CATEGORY;

         if (startsWithCategory) {
            section[0].set("weight", weight);
            weight += WEIGHT_GAP;

            // Assignments/requests that follow this category header (up to the next category)
            const childTasks = section.slice(1);
            childTasks.sort((a: any, b: any) => {
               const seqA = a.getData("job_title_sequence") ?? Number.MAX_SAFE_INTEGER;
               const seqB = b.getData("job_title_sequence") ?? Number.MAX_SAFE_INTEGER;
               if (seqA !== seqB) return seqA - seqB;
               const nameA = (a.getData("name") ?? "").toLowerCase();
               const nameB = (b.getData("name") ?? "").toLowerCase();
               return nameA.localeCompare(nameB);
            });

            for (const task of childTasks) {
               task.set("weight", weight);
               weight += WEIGHT_GAP;
            }
         } else {
            section.sort((a: any, b: any) => {
               const seqA = a.getData("job_title_sequence") ?? Number.MAX_SAFE_INTEGER;
               const seqB = b.getData("job_title_sequence") ?? Number.MAX_SAFE_INTEGER;
               if (seqA !== seqB) return seqA - seqB;
               const nameA = (a.getData("name") ?? "").toLowerCase();
               const nameB = (b.getData("name") ?? "").toLowerCase();
               return nameA.localeCompare(nameB);
            });

            for (const task of section) {
               task.set("weight", weight);
               weight += WEIGHT_GAP;
            }
         }
      }
   }

   taskStore.sort({ field: "weight", ascending: true });
   projectModel.acceptChanges();
   projectModel.resumeAutoSync();
}

/**
 * Formats a number as a percentage using locale-specific formatting
 * @param number - The number to format as a percentage
 * @param locale - The locale to use for formatting (defaults to "en")
 * @returns The formatted percentage string
 */
export function formatNumberWithPercentage(number: number, locale: string = "en") {
   const numberOptions: NumberOptions = {
      locale: locale,
      percent: "standard",
   };

   const formatter = new NumberFormatter(numberOptions);
   return formatter.formatNumber(number);
}
